// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module ChartCard
 * @package @geenius/analytics/react-css
 * @description Renders the React CSS chart card used to visualize simple
 * analytics series such as trends, counts, and comparisons. The component
 * converts shared chart points into a lightweight bar-style presentation.
 */

import type { ChartDataPoint } from "@geenius/analytics";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Tooltip,
} from "@geenius/ui/react-css";
import type React from "react";
import { useId } from "react";
import { cx } from "../lib/cx";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

/**
 * Props for rendering the React CSS chart card.
 *
 * @property title Section heading displayed above the chart body.
 * @property data Ordered data points rendered as bars.
 * @property type Optional chart mode metadata for consumers.
 * @property height Optional pixel height for the chart body.
 * @property isLoading Whether the chart should render placeholder bars.
 * @property className Optional class name merged into the card wrapper.
 */
export interface ChartCardProps {
  title: string;
  data: readonly ChartDataPoint[];
  type?: "bar" | "line";
  height?: number;
  isLoading?: boolean;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  className?: string;
}

/**
 * Renders a React CSS chart card for a list of analytics datapoints.
 *
 * @param props Chart metadata and datapoints to visualize.
 * @returns A chart card element with proportional bars.
 */
export function ChartCard(props: ChartCardProps): React.ReactElement {
  const summaryId = useId();
  if (props.error) {
    return (
      <Card className={props.className}>
        <CardHeader>
          <CardTitle className="gn-analytics-chart__title">
            {props.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="gn-analytics-chart__card">
          <ErrorState error={props.error} onRetry={props.onRetry} />
        </CardContent>
      </Card>
    );
  }

  if (props.isLoading) {
    return (
      <Card className={props.className}>
        <CardHeader>
          <CardTitle className="gn-analytics-chart__title">
            {props.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="gn-analytics-chart__card">
          <div
            className="gn-analytics-chart__loading"
            data-analytics-chart-loading
            aria-busy="true"
            role="status"
          >
            {Array.from(
              { length: 6 },
              (_, index) => `chart-skeleton-${index}`,
            ).map((skeletonId, index) => (
              <Skeleton
                key={skeletonId}
                className="gn-analytics-chart__bar-skeleton"
                height={48 + index * 18}
              />
            ))}
            <span className="gn-analytics-a11y__sr-only">
              Loading {props.title}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const values = props.data.map((point) =>
    Number.isFinite(point.value) ? Math.max(0, point.value) : 0,
  );
  const max = Math.max(...values, 1);

  return (
    <Card className={props.className}>
      <CardHeader>
        <CardTitle className="gn-analytics-chart__title">
          {props.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="gn-analytics-chart__card">
        <div
          className="gn-analytics-chart__body"
          data-analytics="chart-body"
          data-chart-type={props.type ?? "bar"}
          role="img"
          aria-label={`${props.title} ${props.type ?? "bar"} chart`}
          aria-describedby={summaryId}
          style={{ height: props.height ?? 200 }}
        >
          <ul id={summaryId} className="gn-analytics-a11y__sr-only">
            {props.data.map((point) => (
              <li key={`${point.label}-${point.value}`}>
                {point.label}: {point.value}
              </li>
            ))}
          </ul>
          <div className="gn-analytics-chart__bars" data-analytics="chart-bars">
            {props.data.map((point, index) => (
              <div
                key={`${point.label}-${point.value}`}
                className="gn-analytics-chart__bar-group"
                data-analytics="chart-bar-group"
              >
                <Tooltip content={`${point.label}: ${point.value}`}>
                  <div
                    className={cx(
                      "gn-analytics-chart__bar",
                      props.type === "line" && "gn-analytics-chart__bar--line",
                    )}
                    data-analytics="chart-bar"
                    style={{
                      height: `${((values[index] ?? 0) / max) * 100}%`,
                    }}
                  />
                </Tooltip>
                <span
                  className="gn-analytics-chart__label"
                  data-analytics="chart-label"
                >
                  {point.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
